import { EditOutlined, MailOutlined } from '@ant-design/icons';
import { Col, Row, Tabs, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { BackButton } from 'src/components/BackButton/BackButton';
import { Button } from 'src/components/Button/Button';
import { DetailsPage } from 'src/components/FormPages/DetailsPage';
import { Link } from 'src/components/Link/Link';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { PartnerDto } from 'src/generated-api-client';
import { useLoadResources } from 'src/hooks/useLoadResources';
import { InvitePartnerDialog } from 'src/pages/PartnerDetailsPage/component/InvitePartnerDialog';
import { GeneralInformationTab } from 'src/pages/PartnerDetailsPage/tabs/GeneralInformationTab';
import { PartnerApplicationsTab } from 'src/pages/PartnerDetailsPage/tabs/PartnerApplicationsTab';
import { ProductInformationTab } from 'src/pages/PartnerDetailsPage/tabs/ProductInformationTab';
import { PartnersStore } from 'src/stores/PartnersStore/PartnersStore';
import { CountryResourceStore } from 'src/stores/Resources/CountryResourceStore';
import { JahezComissionTypeResourceStore } from 'src/stores/Resources/JahezComissionTypeResourceStore';
import { PartnerTypeResourceStore } from 'src/stores/Resources/PartnerTypeResourceStore';
import { ProductOfferingTypeResourceStore } from 'src/stores/Resources/ProductOfferingTypeResourceStore';
import { SpecializationResourceStore } from 'src/stores/Resources/SpecializationResourceStore';
import { CssColors } from 'src/styles/CssVariables';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import styled from 'styled-components';

export const PartnerDetailsPage = observer(() => {
    const partner = PartnersStore.itemLoader?.data as PartnerDto | null;
    const { isLoadingResources } = useLoadResources([
        CountryResourceStore,
        SpecializationResourceStore,
        ProductOfferingTypeResourceStore,
        PartnerTypeResourceStore,
        JahezComissionTypeResourceStore,
    ]);

    const detailsUrl =
        partner?.id !== undefined
            ? EntityLinks.partners.itemEdit?.(partner?.id)
            : undefined;

    return (
        <DetailsPage
            pageName="partner-details"
            store={PartnersStore as unknown as BasicStore<Entity>}
            isLoading={isLoadingResources}
            headerTopPanel={<BackButton to={EntityLinks.partners.list?.()} />}
        >
            <Row gutter={16}>
                <Col flex={1}>
                    <Typography.Title level={3}>
                        <StyledIdWrapper>{partner?.number}</StyledIdWrapper> -{' '}
                        {partner?.arabicName}
                    </Typography.Title>
                </Col>
                <Col>
                    <ButtonsGroupStyled>
                        <Link
                            to={detailsUrl}
                            button
                            buttonType="default"
                            icon={<EditOutlined />}
                        >
                            {t(
                                'App.Pages.PartnerDetails.Button.Edit',
                                'Update partner',
                            )}
                        </Link>

                        <Button
                            type="primary"
                            onClick={() => {
                                partner?.id &&
                                    InvitePartnerDialog.render({
                                        partnerId: partner.id,
                                    });
                            }}
                        >
                            <MailOutlined />
                            {t(
                                'App.Pages.PartnerDetails.Button.InviteToPortal',
                                'Invite to portal',
                            )}
                        </Button>
                    </ButtonsGroupStyled>
                </Col>
            </Row>

            <Tabs
                items={[
                    {
                        key: 'general-information',
                        label: t(
                            'App.Pages.PartnerDetails.Tab.GeneralInformation',
                            'General information',
                        ),
                        children: <GeneralInformationTab partner={partner} />,
                    },
                    {
                        key: 'products-information',
                        label: t(
                            'App.Pages.PartnerDetails.Tab.ProductsInformation',
                            'Product info',
                        ),
                        children: <ProductInformationTab partner={partner} />,
                    },
                    {
                        key: 'applications-information',
                        label: t(
                            'App.Pages.PartnerDetails.Tab.ApplicationsInformation',
                            'Applications',
                        ),
                        children: <PartnerApplicationsTab />,
                    },
                ]}
            />
        </DetailsPage>
    );
});

const ButtonsGroupStyled = styled.div`
    display: flex;
    align-items: center;

    & > *:not(:last-child) {
        margin-right: 12px;
    }
`;

const StyledIdWrapper = styled.span`
    font-size: inherit;
    color: ${CssColors.gray};
`;
